import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Clock, CheckCircle, XCircle, FileText, 
  TrendingUp, AlertCircle, Loader2, Plus, ArrowRight,
  Info, BanknoteIcon, Receipt, History, ExternalLink, Calendar
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import ProducerLayout from '../../components/producer/ProducerLayout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';

const ProducerSettlementsPage = () => {
  const [earnings, setEarnings] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [producer, setProducer] = useState(null);
  const [detailModal, setDetailModal] = useState({ open: false, settlement: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Get producer info
      const producerRes = await api.get('/producers/me');
      setProducer(producerRes.data);
      
      const producerId = producerRes.data.id;
      
      // Get earnings
      const earningsRes = await api.get(`/settlements/my-earnings?entity_type=producer&entity_id=${producerId}`);
      setEarnings(earningsRes.data);
      
      // Get settlements history
      const settlementsRes = await api.get(`/settlements/my-settlements?entity_type=producer&entity_id=${producerId}`);
      setSettlements(settlementsRes.data.settlements || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSettlement = async () => {
    const amount = parseFloat(requestAmount);
    if (!amount || amount <= 0) {
      toast.error('Ingresá un monto válido');
      return;
    }
    
    if (amount > (earnings?.available_balance || 0)) {
      toast.error(`El monto máximo disponible es ${formatCurrency(earnings?.available_balance)}`);
      return;
    }
    
    try {
      setRequesting(true);
      await api.post('/settlements/request', {
        entity_type: 'producer',
        entity_id: producer.id,
        amount: amount,
        description: 'Solicitud de liquidación'
      });
      
      toast.success('Solicitud enviada - Recibirás un email de confirmación');
      setShowRequestModal(false);
      setRequestAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setRequesting(false);
    }
  };

  const getStatusConfig = (status) => {
    const config = {
      pending: { 
        bg: 'bg-amber-500/10', 
        border: 'border-amber-500/30', 
        text: 'text-amber-400',
        icon: Clock,
        label: 'En Revisión'
      },
      approved: { 
        bg: 'bg-blue-500/10', 
        border: 'border-blue-500/30', 
        text: 'text-blue-400',
        icon: CheckCircle,
        label: 'Aprobada'
      },
      paid: { 
        bg: 'bg-emerald-500/10', 
        border: 'border-emerald-500/30', 
        text: 'text-emerald-400',
        icon: BanknoteIcon,
        label: 'Pagada'
      },
      rejected: { 
        bg: 'bg-red-500/10', 
        border: 'border-red-500/30', 
        text: 'text-red-400',
        icon: XCircle,
        label: 'Rechazada'
      }
    };
    return config[status] || config.pending;
  };

  const getStatusBadge = (status) => {
    const c = getStatusConfig(status);
    const Icon = c.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${c.bg} ${c.border} ${c.text}`}>
        <Icon size={12} />
        {c.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <ProducerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="animate-spin text-violet-500" size={32} />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout>
      <div className="space-y-6" data-testid="producer-settlements-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <DollarSign className="text-white" size={22} />
              </div>
              Mis Liquidaciones
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Gestiona tus cobros por ventas de entradas
            </p>
          </div>
          
          {earnings?.available_balance > 0 && (
            <Button
              onClick={() => setShowRequestModal(true)}
              className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg shadow-emerald-500/20"
            >
              <Plus size={18} className="mr-2" />
              Solicitar Liquidación
            </Button>
          )}
        </div>

        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
          <Info className="text-blue-400 mt-0.5 flex-shrink-0" size={20} />
          <div>
            <p className="text-blue-300 font-medium text-sm">Información sobre Pagos</p>
            <p className="text-blue-200/70 text-sm mt-1">
              Los montos mostrados son <strong>ingresos netos</strong> (sin costos de servicio de la plataforma). 
              Una vez aprobada tu solicitud, el pago puede demorar entre <strong>24 y 48 horas hábiles</strong> en acreditarse según tu banco.
            </p>
          </div>
        </div>

        {/* Balance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">Ingreso Neto Total</p>
                <p className="text-3xl font-black text-white mt-2">{formatCurrency(earnings?.total_earned)}</p>
                <p className="text-zinc-500 text-sm mt-1">Sin costos de servicio</p>
              </div>
              <div className="w-14 h-14 bg-violet-500/20 rounded-2xl flex items-center justify-center">
                <TrendingUp className="text-violet-400" size={28} />
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-700/50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wide">Ya Liquidado</p>
                <p className="text-3xl font-black text-white mt-2">{formatCurrency(earnings?.total_settled)}</p>
                <p className="text-zinc-500 text-sm mt-1">Pagos transferidos</p>
              </div>
              <div className="w-14 h-14 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                <CheckCircle className="text-blue-400" size={28} />
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/30 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wide">Disponible para Liquidar</p>
                <p className="text-3xl font-black text-emerald-400 mt-2">{formatCurrency(earnings?.available_balance)}</p>
                <p className="text-emerald-400/60 text-sm mt-1">Listo para solicitar</p>
              </div>
              <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center">
                <DollarSign className="text-emerald-400" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* Earnings by Event */}
        {earnings?.earnings_detail?.length > 0 && (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-2">
              <Calendar className="text-violet-400" size={18} />
              <h3 className="font-bold text-white">Detalle por Evento</h3>
            </div>
            <div className="divide-y divide-zinc-800">
              {earnings.earnings_detail.map((item, index) => (
                <div key={index} className="px-6 py-4 hover:bg-zinc-800/30 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">{item.event_title}</p>
                      <p className="text-sm text-zinc-500 mt-0.5">
                        Generado: <span className="text-zinc-300">{formatCurrency(item.total_earned)}</span>
                        {' • '}
                        Liquidado: <span className="text-zinc-300">{formatCurrency(item.already_settled)}</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-emerald-400 font-bold text-lg">{formatCurrency(item.available)}</p>
                      <p className="text-xs text-zinc-500">Disponible</p>
                    </div>
                  </div>
                  {(item.total_discounts || 0) > 0 && (
                    <div className="mt-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded text-xs flex items-center justify-between" data-testid={`producer-settlement-discount-${item.event_id}`}>
                      <span className="text-amber-400">Descuentos aplicados en este evento</span>
                      <span className="text-amber-400 font-semibold">−{formatCurrency(item.total_discounts)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settlements History */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-800 flex items-center gap-2">
            <History className="text-violet-400" size={18} />
            <h3 className="font-bold text-white">Historial de Liquidaciones</h3>
          </div>
          
          {settlements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
              <DollarSign size={48} className="mb-4 opacity-30" />
              <p className="font-medium">No tenés liquidaciones todavía</p>
              <p className="text-sm mt-1">Las solicitudes aparecerán aquí</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {settlements.map((settlement) => {
                const statusConfig = getStatusConfig(settlement.status);
                
                return (
                  <div 
                    key={settlement.id} 
                    className="px-6 py-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors cursor-pointer"
                    onClick={() => setDetailModal({ open: true, settlement })}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusConfig.bg}`}>
                        <statusConfig.icon className={statusConfig.text} size={22} />
                      </div>
                      <div>
                        <p className="font-bold text-white text-lg">{formatCurrency(settlement.amount)}</p>
                        <p className="text-sm text-zinc-500">
                          {settlement.event_title || 'General'} • {formatDate(settlement.requested_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusBadge(settlement.status)}
                      {settlement.payment_proof_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(settlement.payment_proof_url, '_blank');
                          }}
                          className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                        >
                          <Receipt size={14} className="mr-1" />
                          Comprobante
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Request Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <DollarSign className="text-emerald-400" size={20} />
                Solicitar Liquidación
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Ingresá el monto que querés solicitar
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Available Balance */}
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                <p className="text-xs text-emerald-400 uppercase tracking-wide">Saldo Disponible</p>
                <p className="text-3xl font-black text-emerald-400 mt-1">
                  {formatCurrency(earnings?.available_balance)}
                </p>
              </div>
              
              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Monto a solicitar
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                    <Input
                      type="number"
                      value={requestAmount}
                      onChange={(e) => setRequestAmount(e.target.value)}
                      placeholder="0"
                      max={earnings?.available_balance}
                      className="pl-8 bg-zinc-800 border-zinc-700 text-lg font-bold"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setRequestAmount(earnings?.available_balance?.toString() || '0')}
                    className="border-zinc-700 whitespace-nowrap"
                  >
                    Todo
                  </Button>
                </div>
              </div>
              
              {/* Info */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 flex items-start gap-2">
                <Info className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                <div className="text-xs text-blue-300">
                  <p className="font-medium mb-1">Proceso de pago:</p>
                  <ul className="list-disc list-inside text-blue-200/70 space-y-0.5">
                    <li>Tu solicitud será revisada por el equipo</li>
                    <li>Una vez aprobada, procesaremos el pago</li>
                    <li>Recibirás un email con el comprobante</li>
                    <li>El pago puede demorar 24-48hs hábiles según tu banco</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowRequestModal(false)}
                className="border-zinc-700"
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleRequestSettlement}
                disabled={!requestAmount || parseFloat(requestAmount) <= 0 || requesting}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {requesting ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <ArrowRight className="mr-2" size={16} />
                )}
                Enviar Solicitud
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={detailModal.open} onOpenChange={(open) => !open && setDetailModal({ open: false, settlement: null })}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <FileText className="text-violet-400" size={20} />
                Detalle de Liquidación
              </DialogTitle>
            </DialogHeader>
            
            {detailModal.settlement && (
              <div className="space-y-4 py-4">
                {/* Amount & Status */}
                <div className="text-center">
                  <p className="text-4xl font-black text-emerald-400">
                    {formatCurrency(detailModal.settlement.amount)}
                  </p>
                  <div className="mt-3">{getStatusBadge(detailModal.settlement.status)}</div>
                </div>
                
                {/* Details */}
                <div className="space-y-3">
                  <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                    <span className="text-zinc-400 text-sm">ID</span>
                    <span className="text-white font-mono text-sm">{detailModal.settlement.id}</span>
                  </div>
                  
                  {detailModal.settlement.event_title && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                      <span className="text-zinc-400 text-sm">Evento</span>
                      <span className="text-white text-sm">{detailModal.settlement.event_title}</span>
                    </div>
                  )}
                  
                  <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                    <span className="text-zinc-400 text-sm">Solicitado</span>
                    <span className="text-white text-sm">{formatDateTime(detailModal.settlement.requested_at)}</span>
                  </div>
                  
                  {detailModal.settlement.approved_at && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                      <span className="text-zinc-400 text-sm">Aprobado</span>
                      <span className="text-white text-sm">{formatDateTime(detailModal.settlement.approved_at)}</span>
                    </div>
                  )}
                  
                  {detailModal.settlement.paid_at && (
                    <div className="bg-zinc-800/50 rounded-lg p-3 flex justify-between">
                      <span className="text-zinc-400 text-sm">Pagado</span>
                      <span className="text-white text-sm">{formatDateTime(detailModal.settlement.paid_at)}</span>
                    </div>
                  )}
                </div>
                
                {/* Rejection Reason */}
                {detailModal.settlement.admin_notes && detailModal.settlement.status === 'rejected' && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <p className="text-xs text-red-400 font-semibold mb-1">Motivo del rechazo:</p>
                    <p className="text-sm text-red-300">{detailModal.settlement.admin_notes}</p>
                  </div>
                )}
                
                {/* Payment Proof */}
                {detailModal.settlement.payment_proof_url && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-500"
                    onClick={() => window.open(detailModal.settlement.payment_proof_url, '_blank')}
                  >
                    <ExternalLink size={16} className="mr-2" />
                    Ver Comprobante de Pago
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </ProducerLayout>
  );
};

export default ProducerSettlementsPage;
